import { Immutable } from "immer";
import React from "react";

import { useFlickity } from "../../../hooks/useFlickity";
import { IConcreteBundle, IProduct } from "../../../models/catalogue";
import { trackUpsellModalAddToCartEvent } from "../../../utils/analytics";
import { addProductToBasket } from "../../../utils/basket";
import { UpsellModalSlide } from "./UpsellModalSlide";
import { UpsellSlideValue } from "./types";

const isAvailable = (bundle: IConcreteBundle): boolean => {
    const product = bundle.suggested_products[0];
    if (!product) {
        return false;
    }
    return product.availability.is_available_to_buy;
};

const findBundleBySlide = (
    bundles: readonly IConcreteBundle[],
    slide: Immutable<UpsellSlideValue>,
): IConcreteBundle | null => {
    return (
        bundles.find((b) => b.bundle_group.id === slide.slide_bundle?.id) ??
        null
    );
};

type IProps = Immutable<{
    slides: ReadonlyArray<UpsellSlideValue>;
    bundles: readonly IConcreteBundle[];
    onProceed: () => void;
}>;

export const UpsellModalSlideCollection = (props: IProps): React.ReactNode => {
    const [buyIsDisabled, setBuyIsDisabled] = React.useState(false);
    const slidesElemRef = React.useRef<HTMLDivElement | null>(null);

    const initialSlide = props.slides.findIndex((s) => s.initial_mobile_slide);
    useFlickity(slidesElemRef, {
        options: {
            cellAlign: "center",
            wrapAround: false,
            imagesLoaded: true,
            prevNextButtons: false,
            initialIndex: initialSlide,
            watchCSS: true,
        },
        deps: [props.slides],
    });

    const onAddToBasket = async (product: IProduct) => {
        setBuyIsDisabled(true);
        try {
            await addProductToBasket(product.url);
        } catch (e) {
            setBuyIsDisabled(false);
            return;
        }
        trackUpsellModalAddToCartEvent(product);
        props.onProceed();
    };

    if (!props.slides) {
        return null;
    }

    const slides = props.slides.map((slide, i) => {
        if (!slide.image) {
            return null;
        }
        const bundle = findBundleBySlide(props.bundles, slide);
        if (!bundle || !isAvailable(bundle)) {
            return null;
        }
        return (
            <UpsellModalSlide
                key={i}
                bundle={bundle}
                imagePath={slide.image.url}
                imageAlt={slide.image.title}
                buyIsDisabled={buyIsDisabled}
                onAddToBasket={onAddToBasket}
            />
        );
    });

    return (
        <div className="upsell-modal__slides" ref={slidesElemRef}>
            {slides}
        </div>
    );
};
